import styled from "@emotion/styled";
import { Thumbnail } from "features/images";
import { Input, Label, ScreenReaderText, Stack } from "atoms";
import { GalleryItemSelectImage } from "../types";

type SelectImagesProps = {
  data: GalleryItemSelectImage[];
  aspectRatio?: React.CSSProperties["aspectRatio"];
  multiple?: boolean;

  // unique ID to group inputs
  groupId: string;
  isSelected: (item: GalleryItemSelectImage) => boolean;
  toggleSelected: (item: GalleryItemSelectImage) => void;
};

function SelectImages({
  data,
  aspectRatio = "auto",
  multiple = false,
  groupId,
  isSelected,
  toggleSelected,
}: SelectImagesProps) {
  return (
    <Stack gap="sm">
      <Grid>
        {data.map((item) => {
          if (item.image !== undefined) {
            return (
              <GridItem key={item.id} style={{ aspectRatio }}>
                <SInput
                  type={multiple ? "checkbox" : "radio"}
                  name={groupId}
                  id={item.image.url}
                  checked={isSelected(item)}
                  onChange={() => toggleSelected(item)}
                />
                <SLabel htmlFor={item.image.url} multiple={multiple}>
                  <ScreenReaderText>{item.title}</ScreenReaderText>
                </SLabel>
                <SThumbnail
                  src={item.image.url300 ?? item.image.url600 ?? item.image.url}
                  aria-hidden
                  containImage={aspectRatio === "auto"}
                />
              </GridItem>
            );
          }
          return null;
        })}
      </Grid>
    </Stack>
  );
}

const Grid = styled.div({
  display: "grid",
  gap: 12,
  gridTemplateColumns: "repeat(auto-fill, minmax(135px, 1fr))",
});

const GridItem = styled.div(({ theme }) => ({
  borderRadius: theme.borderRadius.sm,
  flex: "0 1 30%",
  position: "relative",
}));

const SInput = styled(Input)(({ theme }) => ({
  // visually hide input element while remaining interactive
  border: 0,
  borderRadius: theme.borderRadius.sm,
  height: 0,
  margin: 0,
  minHeight: 0,
  opacity: 0,
  outline: 0,
  overflow: "hidden",
  position: "absolute",
  width: 0,
  "&:focus, &:focus-visible": {
    outline: 0,
  },
}));

const SLabel = styled(Label)<{ multiple: boolean }>(({ multiple, theme }) => ({
  borderRadius: theme.borderRadius.sm,
  boxShadow: `0 0 0 5px ${theme.colors.transparent}`,
  inset: 0,
  outline: "3px solid",
  outlineColor: theme.colors.transparent,
  outlineOffset: 2,
  position: "absolute",
  transition: ".2s ease all",
  zIndex: 2,
  "&::after": {
    alignItems: "center",
    backgroundColor: theme.colors.bg,
    border: "2px solid",
    borderColor: theme.colors.fg,
    borderRadius: multiple ? theme.borderRadius.sm : theme.borderRadius.round,
    color: theme.colors.fg,
    content: "'✓'",
    display: "flex",
    fontWeight: "bold",
    height: 26,
    justifyContent: "center",
    lineHeight: 1,
    opacity: 0,
    paddingTop: 3,
    pointerEvents: "none",
    position: "absolute",
    right: 5,
    top: 5,
    transition: ".2s ease all",
    width: 26,
  },
  // input interactions
  "input:checked + &": {
    outlineColor: theme.colors.fg,
  },
  "input:checked + &::after": {
    opacity: 1,
  },
  "input:focus-visible + &": {
    boxShadow: `0 0 0 5px ${theme.colors.fg}`,
  },
  "input:checked + &:hover, input:checked:focus-visible + &:hover, &:hover": {
    boxShadow: `0 0 0 5px ${theme.colors.fg70}`,
    outlineColor: theme.colors.fg70,
  },
}));

const SThumbnail = styled(Thumbnail)<{ containImage: boolean }>(
  ({ containImage, theme }) => ({
    "&,img": {
      borderRadius: theme.borderRadius.sm,
      height: "100%",
      objectFit: containImage ? "contain" : "cover",
      objectPosition: "center",
      pointerEvents: "none",
      position: "relative",
      width: "100%",
      zIndex: 0,
    },
  })
);

export { SelectImages };
